import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Compass,
  User,
  Sparkles,
  Users,
  MessageSquare,
  Bookmark,
  CheckCircle,
  Heart,
  ListVideo,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserLibrary } from '../../contexts/UserLibraryContext';
import { useLists, type WatchlistMeta as Watchlist } from '../../contexts/ListsContext';
import { CreateListModal, type MediaFilter } from '../CreateListModal';
import { SocialPanel } from './SocialPanel';
import { useFollowedWatchlists, type FollowedWatchlist } from '../../hooks/useWatchlistFollows';
import { useListPrefetch } from '../../hooks/usePrefetch';

// Re-export type for use in FollowedListItem component
type FollowedList = FollowedWatchlist;

// LocalStorage key for sidebar state
const SIDEBAR_COLLAPSED_KEY = 'filmple:sidebar_collapsed';

// PART B: LocalStorage key for section collapse state (per user)
const SECTIONS_STATE_KEY = (userId: string) => `filmple.sidebar.sections.v1.${userId}`;

// PART B: Default section state (all open)
type SectionState = { library: boolean; yourLists: boolean; sharedLists: boolean; following: boolean };
const DEFAULT_SECTION_STATE: SectionState = { library: true, yourLists: true, sharedLists: true, following: true };

// LocalStorage key for followed lists cache (per user) - 30 minute TTL
const FOLLOWED_LISTS_CACHE_KEY = (userId: string) => `filmple:followedLists:${userId}`;
const FOLLOWED_LISTS_TTL_MS = 30 * 60 * 1000; // 30 minutes

// LocalStorage key for catalog custom lists cache (per user) - 30 minute TTL
const CUSTOM_LISTS_CACHE_KEY = (userId: string) => `filmple:catalogCustomLists:${userId}`;
const CUSTOM_LISTS_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Cached custom list shape (minimal for sidebar)
type CachedCustomList = {
  id: string;
  name: string;
  slug: string | null;
  item_count: number;
  is_default: boolean;
  media_filter: 'movie' | 'tv' | 'mixed';
  visibility: 'public' | 'private' | 'friends';
  collaborator_role?: 'editor' | 'viewer' | null;
  has_collaborators?: boolean; // PART A: "shared out" indicator for owned lists
};

// Tooltip rendered via portal (outside overflow container)
function TooltipPortal({ label, targetRect }: { label: string; targetRect: DOMRect | null }) {
  if (!targetRect) return null;

  return createPortal(
    <div
      className="fixed z-40 pointer-events-none"
      style={{
        top: targetRect.top + targetRect.height / 2,
        left: targetRect.right + 12,
        transform: 'translateY(-50%)',
      }}
    >
      <div className="bg-white text-zinc-900 rounded-xl px-3 py-2 text-sm font-medium shadow-lg whitespace-nowrap">
        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rotate-45" />
        {label}
      </div>
    </div>,
    document.body
  );
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { lists: watchlists, createWatchlist, isHydrated: listsHydrated } = useLists();
  const { isHydrated, watchlistCount, watchedCount, favoritesCount, defaultListIds } = useUserLibrary();

  // React Query hook for followed lists (Phase 3.1 - single source of truth)
  const { followedLists: rqFollowedLists, isLoading: followedListsLoading } = useFollowedWatchlists();

  // Prefetch hook for list detail pages (Phase 5 - hover prefetch)
  const { prefetch: prefetchList, cancel: cancelListPrefetch } = useListPrefetch();

  // Derive counts from UserLibraryContext for default lists (optimistic UI)
  // Falls back to metadata until hydrated
  const getDefaultListCount = (slug: string, metadataCount: number): number => {
    if (!isHydrated) return metadataCount;
    switch (slug) {
      case 'watchlist': return watchlistCount;
      case 'watched': return watchedCount;
      case 'favorites': return favoritesCount;
      default: return metadataCount;
    }
  };

  // Sidebar collapse state - persisted in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Re-sync sidebar on tab visibility change to fix rendering issues
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Force a re-render by reading the current localStorage value
        // This ensures CSS and React state are in sync after tab switch
        try {
          const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
          const shouldBeCollapsed = stored === 'true';
          setIsCollapsed(shouldBeCollapsed);
        } catch {
          // Ignore storage errors
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also handle window focus for additional coverage
    const handleFocus = () => {
      try {
        const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        const shouldBeCollapsed = stored === 'true';
        setIsCollapsed(shouldBeCollapsed);
      } catch {
        // Ignore storage errors
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Warm-start cache for followed lists (renders instantly before contextFollowedLists populates)
  const [cachedFollowedLists, setCachedFollowedLists] = useState<FollowedList[]>(() => {
    if (!user?.id) return [];
    try {
      const raw = localStorage.getItem(FOLLOWED_LISTS_CACHE_KEY(user.id));
      if (!raw) return [];
      const parsed = JSON.parse(raw) as { fetchedAtMs: number; lists: FollowedList[] };
      // Check TTL
      if (Date.now() - parsed.fetchedAtMs > FOLLOWED_LISTS_TTL_MS) return [];
      return parsed.lists || [];
    } catch {
      return [];
    }
  });

  // Load cache when user.id becomes available (initializer won't rerun if user was null on first render)
  useEffect(() => {
    if (!user?.id) return;
    // Only load cache if React Query hasn't loaded yet (warm-start scenario)
    if (!followedListsLoading) return;
    try {
      const raw = localStorage.getItem(FOLLOWED_LISTS_CACHE_KEY(user.id));
      if (!raw) return;
      const parsed = JSON.parse(raw) as { fetchedAtMs: number; lists: FollowedList[] };
      if (Date.now() - parsed.fetchedAtMs > FOLLOWED_LISTS_TTL_MS) return;
      setCachedFollowedLists(parsed.lists || []);
    } catch {
      // Ignore storage errors
    }
  }, [user?.id, followedListsLoading]);

  // Write cache whenever rqFollowedLists changes (after query loads, even if empty to clear stale cache)
  useEffect(() => {
    if (!user?.id) return;
    if (followedListsLoading) return; // Don't write cache until query loads
    try {
      localStorage.setItem(
        FOLLOWED_LISTS_CACHE_KEY(user.id),
        JSON.stringify({ fetchedAtMs: Date.now(), lists: rqFollowedLists })
      );
      // Also update local cached state so it stays in sync
      setCachedFollowedLists(rqFollowedLists);
    } catch {
      // Ignore storage errors
    }
  }, [user?.id, followedListsLoading, rqFollowedLists]);

  // Followed lists to render (Phase 3.1 - uses React Query as source of truth):
  // - After query loads: always use rqFollowedLists (even if empty = no followed lists)
  // - Before query loads: use rqFollowedLists if available, else warm-start from cache
  const followedListsToRender = !followedListsLoading
    ? rqFollowedLists
    : (rqFollowedLists.length > 0 ? rqFollowedLists : cachedFollowedLists);

  // Warm-start cache for catalog custom lists (renders instantly before ListsContext refresh)
  const [cachedCatalogCustomLists, setCachedCatalogCustomLists] = useState<CachedCustomList[]>(() => {
    if (!user?.id) return [];
    try {
      const raw = localStorage.getItem(CUSTOM_LISTS_CACHE_KEY(user.id));
      if (!raw) return [];
      const parsed = JSON.parse(raw) as { fetchedAtMs: number; lists: CachedCustomList[] };
      if (Date.now() - parsed.fetchedAtMs > CUSTOM_LISTS_TTL_MS) return [];
      return parsed.lists || [];
    } catch {
      return [];
    }
  });

  // Load custom lists cache when user.id becomes available (initializer won't rerun if user was null on first render)
  useEffect(() => {
    if (!user?.id) return;
    if (listsHydrated) return; // Only load cache if not yet hydrated
    try {
      const raw = localStorage.getItem(CUSTOM_LISTS_CACHE_KEY(user.id));
      if (!raw) return;
      const parsed = JSON.parse(raw) as { fetchedAtMs: number; lists: CachedCustomList[] };
      if (Date.now() - parsed.fetchedAtMs > CUSTOM_LISTS_TTL_MS) return;
      setCachedCatalogCustomLists(parsed.lists || []);
    } catch {
      // Ignore storage errors
    }
  }, [user?.id, listsHydrated]);

  // Create list modal state
  const [showCreateListModal, setShowCreateListModal] = useState(false);

  // Social panel state
  const [socialPanelOpen, setSocialPanelOpen] = useState(false);

  // PART B: Collapsible section state (per-user persistence)
  const [sectionsOpen, setSectionsOpen] = useState<SectionState>(() => {
    if (!user?.id) return DEFAULT_SECTION_STATE;
    try {
      const raw = localStorage.getItem(SECTIONS_STATE_KEY(user.id));
      if (!raw) return DEFAULT_SECTION_STATE;
      const parsed = JSON.parse(raw);
      // Validate and merge with defaults (in case schema changes)
      return {
        library: typeof parsed.library === 'boolean' ? parsed.library : true,
        yourLists: typeof parsed.yourLists === 'boolean' ? parsed.yourLists : true,
        sharedLists: typeof parsed.sharedLists === 'boolean' ? parsed.sharedLists : true,
        following: typeof parsed.following === 'boolean' ? parsed.following : true,
      };
    } catch {
      return DEFAULT_SECTION_STATE;
    }
  });

  // PART B: Load sections state when user.id becomes available
  useEffect(() => {
    if (!user?.id) {
      setSectionsOpen(DEFAULT_SECTION_STATE);
      return;
    }
    try {
      const raw = localStorage.getItem(SECTIONS_STATE_KEY(user.id));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setSectionsOpen({
        library: typeof parsed.library === 'boolean' ? parsed.library : true,
        yourLists: typeof parsed.yourLists === 'boolean' ? parsed.yourLists : true,
        sharedLists: typeof parsed.sharedLists === 'boolean' ? parsed.sharedLists : true,
        following: typeof parsed.following === 'boolean' ? parsed.following : true,
      });
    } catch {
      // Ignore parse errors, keep defaults
    }
  }, [user?.id]);

  // PART B: Persist sections state to localStorage
  useEffect(() => {
    if (!user?.id) return;
    try {
      localStorage.setItem(SECTIONS_STATE_KEY(user.id), JSON.stringify(sectionsOpen));
    } catch {
      // Ignore storage errors
    }
  }, [user?.id, sectionsOpen]);

  // PART B: Toggle section helper
  const toggleSection = (section: keyof SectionState) => {
    setSectionsOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Footer tooltip states (for expand button and user profile)
  const expandBtnRef = useRef<HTMLDivElement>(null);
  const [expandTooltipRect, setExpandTooltipRect] = useState<DOMRect | null>(null);
  const userBtnRef = useRef<HTMLDivElement>(null);
  const [userTooltipRect, setUserTooltipRect] = useState<DOMRect | null>(null);

  // Toggle sidebar collapse
  const toggleCollapsed = () => {
    // Clear tooltips immediately before state change to prevent floating tooltips
    setExpandTooltipRect(null);
    setUserTooltipRect(null);

    setIsCollapsed((prev) => {
      const newValue = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
        // Dispatch custom event for immediate sync with AppShell
        window.dispatchEvent(new CustomEvent('sidebar-collapse-change'));
      } catch {
        // Ignore storage errors
      }
      return newValue;
    });
  };

  // Expand sidebar helper (used by logo click and nav items)
  const expandSidebar = () => {
    if (!isCollapsed) return;

    // Clear any floating tooltips before expanding
    setExpandTooltipRect(null);
    setUserTooltipRect(null);

    setIsCollapsed(false);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'false');
      window.dispatchEvent(new CustomEvent('sidebar-collapse-change'));
    } catch {
      // ignore
    }
  };


  // Helper to check if a path is active
  const isActive = (path: string) => location.pathname === path;
  const isListActive = (id: string) => location.pathname === `/list/${id}`;

  // Separate default lists and custom lists
  const defaultLists = watchlists.filter((w) => w.is_default);

  // Deterministic sort comparator: case-insensitive name, then id (prevents flicker on source switch)
  const listComparator = (a: { name: string; id: string }, b: { name: string; id: string }) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();
    if (nameA !== nameB) return nameA.localeCompare(nameB);
    return a.id.localeCompare(b.id);
  };

  const catalogCustomLists = useMemo(
    () => watchlists.filter((w) => !w.is_default).sort(listComparator),
    [watchlists]
  );


  // Write-through cache: when ListsContext is hydrated, cache catalogCustomLists (even if empty to clear stale)
  useEffect(() => {
    if (!user?.id) return;
    if (!listsHydrated) return; // Don't write until hydrated
    try {
      const toCache: CachedCustomList[] = catalogCustomLists.map((l) => ({
        id: l.id,
        name: l.name,
        slug: l.slug ?? null,
        item_count: l.item_count ?? 0,
        is_default: false,
        media_filter: l.media_filter ?? 'mixed',
        visibility: l.visibility ?? 'private',
        collaborator_role: l.collaborator_role ?? null,
        has_collaborators: l.has_collaborators ?? undefined,
      }));
      localStorage.setItem(
        CUSTOM_LISTS_CACHE_KEY(user.id),
        JSON.stringify({ fetchedAtMs: Date.now(), lists: toCache })
      );
      setCachedCatalogCustomLists(toCache);
    } catch {
      // Ignore storage errors
    }
  }, [user?.id, listsHydrated, catalogCustomLists]);

  // Custom lists to render:
  // - After hydration: always use catalogCustomLists (even if empty = no custom lists)
  // - Before hydration: use catalogCustomLists if non-empty, else warm-start from cache
  const customLists = listsHydrated
    ? catalogCustomLists
    : (catalogCustomLists.length > 0 ? catalogCustomLists : cachedCatalogCustomLists);

  // Split custom lists into owned vs shared (based on collaborator_role)
  // This must use customLists (not catalogCustomLists) to respect warm-start cache
  const ownedCustomLists = useMemo(
    () => customLists.filter((w) => !w.collaborator_role),
    [customLists]
  );
  const sharedCustomLists = useMemo(
    () => customLists.filter((w) => w.collaborator_role === 'editor' || w.collaborator_role === 'viewer'),
    [customLists]
  );

  // Get specific default lists
  const watchlistList = defaultLists.find((w) => w.slug === 'watchlist');
  const watchedList = defaultLists.find((w) => w.slug === 'watched');
  const favoritesList = defaultLists.find((w) => w.slug === 'favorites');

  const watchedId = defaultListIds?.watched || watchedList?.id;
  const watchlistId = defaultListIds?.watchlist || watchlistList?.id;
  const favoritesId = defaultListIds?.favorites || favoritesList?.id;

  // Handle list creation
  const handleListCreated = (list: { id: string; name: string; description?: string; visibility: string; media_filter: MediaFilter }) => {
    setShowCreateListModal(false);
    navigate(`/list/${list.id}`);
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    const name = profile?.display_name || profile?.username || '';
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Nav item component with tooltip support
  const NavItem = ({
    to,
    icon: Icon,
    label,
    active,
  }: {
    to: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    label: string;
    active: boolean;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);

    const handleClick = () => {
      if (isCollapsed) {
        setTooltipRect(null);
        expandSidebar();
      }
    };

    return (
      <div
        ref={itemRef}
        className="relative"
        onMouseEnter={() => isCollapsed && itemRef.current && setTooltipRect(itemRef.current.getBoundingClientRect())}
        onMouseLeave={() => setTooltipRect(null)}
      >
        {active && (
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-teal-500 rounded-r" />
        )}
        <Link
          to={to}
          onClick={handleClick}
          className={`flex items-center gap-3 w-full py-2 px-3 rounded-lg transition-colors ${
            isCollapsed ? 'justify-center' : ''
          } ${
            active
              ? 'text-teal-400 bg-teal-500/10'
              : 'text-white hover:text-white hover:bg-[#1A1F26]/50'
          }`}
        >
          <Icon size={20} className={`shrink-0 ${active ? 'text-teal-400' : 'text-zinc-400'}`} />
          {!isCollapsed && <span className="text-[15px] font-medium whitespace-nowrap">{label}</span>}
        </Link>
        {isCollapsed && <TooltipPortal label={label} targetRect={tooltipRect} />}
      </div>
    );
  };

  // Social nav item component
  const SocialNavItem = ({
    icon: Icon,
    label,
    active,
    onClick,
  }: {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    label: string;
    active: boolean;
    onClick: () => void;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);

    const handleClick = () => {
      if (isCollapsed) {
        setTooltipRect(null);
        expandSidebar();
      }
      onClick();
    };

    return (
      <div
        ref={itemRef}
        className="relative"
        onMouseEnter={() => isCollapsed && itemRef.current && setTooltipRect(itemRef.current.getBoundingClientRect())}
        onMouseLeave={() => setTooltipRect(null)}
      >
        {active && (
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-teal-500 rounded-r" />
        )}
        <button
          type="button"
          onClick={handleClick}
          className={`flex items-center gap-3 w-full py-2 px-3 rounded-lg transition-colors ${
            isCollapsed ? 'justify-center' : ''
          } ${
            active
              ? 'text-teal-400 bg-teal-500/10'
              : 'text-white hover:text-white hover:bg-[#1A1F26]/50'
          }`}
        >
          <Icon size={20} className={`shrink-0 ${active ? 'text-teal-400' : 'text-zinc-400'}`} />
          {!isCollapsed && <span className="text-[15px] font-medium whitespace-nowrap">{label}</span>}
        </button>
        {isCollapsed && <TooltipPortal label={label} targetRect={tooltipRect} />}
      </div>
    );
  };

  // Library item component
  const LibraryItem = ({
    to,
    icon: Icon,
    label,
    count,
    active,
    listId,
  }: {
    to: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    label: string;
    count?: number;
    active: boolean;
    listId?: string;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
    const tooltipLabel = count !== undefined && count > 0 ? `${label} (${count})` : label;

    const handleMouseEnter = () => {
      if (isCollapsed && itemRef.current) {
        setTooltipRect(itemRef.current.getBoundingClientRect());
      }
      // Prefetch list detail on hover
      if (listId) {
        prefetchList(listId);
      }
    };

    const handleMouseLeave = () => {
      setTooltipRect(null);
      cancelListPrefetch();
    };

    return (
      <div
        ref={itemRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {active && (
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-teal-500 rounded-r" />
        )}
        <Link
          to={to}
          className={`flex items-center gap-3 w-full py-2 px-3 rounded-lg transition-colors ${
            isCollapsed ? 'justify-center' : ''
          } ${
            active
              ? 'text-teal-400 bg-teal-500/10'
              : 'text-white hover:text-white hover:bg-[#1A1F26]/50'
          }`}
        >
          <Icon size={20} className={`shrink-0 ${active ? 'text-teal-400' : 'text-zinc-400'}`} />
          {!isCollapsed && (
            <span className="flex items-center justify-between w-full">
              <span className="text-[15px] font-medium whitespace-nowrap">{label}</span>
              {count !== undefined && count > 0 && (
                <span className="text-sm text-zinc-400 tabular-nums">{count}</span>
              )}
            </span>
          )}
        </Link>
        {isCollapsed && <TooltipPortal label={tooltipLabel} targetRect={tooltipRect} />}
      </div>
    );
  };

  // Custom list item component
  const CustomListItem = ({
    list,
    active,
  }: {
    list: Watchlist;
    active: boolean;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
    const tooltipLabel = list.item_count > 0 ? `${list.name} (${list.item_count})` : list.name;

    const handleMouseEnter = () => {
      if (isCollapsed && itemRef.current) {
        setTooltipRect(itemRef.current.getBoundingClientRect());
      }
      // Prefetch list detail on hover
      prefetchList(list.id);
    };

    const handleMouseLeave = () => {
      setTooltipRect(null);
      cancelListPrefetch();
    };

    return (
      <div
        ref={itemRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {active && (
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-teal-500 rounded-r" />
        )}
        <Link
          to={`/list/${list.id}`}
          className={`flex items-center gap-3 w-full py-2 px-3 rounded-lg transition-colors text-sm ${
            isCollapsed ? 'justify-center' : ''
          } ${
            active
              ? 'text-teal-300 bg-teal-500/10'
              : 'text-zinc-400 hover:text-white hover:bg-[#1A1F26]/30'
          }`}
        >
          {/* Icon with optional "shared out" badge overlay in collapsed mode */}
          <div className="relative shrink-0">
            <ListVideo size={16} className={active ? 'text-teal-300' : 'text-zinc-500'} />
            {/* PART A: Collapsed mode - tiny Users badge overlay */}
            {isCollapsed && list.has_collaborators && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-teal-500 rounded-full flex items-center justify-center">
                <Users size={7} className="text-white" />
              </div>
            )}
          </div>
          {!isCollapsed && (
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <span className="truncate">{list.name}</span>
              {/* PART A: Expanded mode - Users badge next to count */}
              {list.has_collaborators && (
                <span title="Shared with collaborators">
                  <Users size={12} className="text-teal-400 shrink-0" />
                </span>
              )}
              {list.item_count > 0 && (
                <span className="text-xs text-zinc-400 tabular-nums shrink-0">{list.item_count}</span>
              )}
            </span>
          )}
        </Link>
        {isCollapsed && <TooltipPortal label={tooltipLabel} targetRect={tooltipRect} />}
      </div>
    );
  };

  // Shared list item component (uses Users icon instead of FolderOpen)
  const SharedListItem = ({
    list,
    active,
  }: {
    list: Watchlist;
    active: boolean;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
    const tooltipLabel = list.item_count > 0 ? `${list.name} (${list.item_count})` : list.name;

    const handleMouseEnter = () => {
      if (isCollapsed && itemRef.current) {
        setTooltipRect(itemRef.current.getBoundingClientRect());
      }
      // Prefetch list detail on hover
      prefetchList(list.id);
    };

    const handleMouseLeave = () => {
      setTooltipRect(null);
      cancelListPrefetch();
    };

    return (
      <div
        ref={itemRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {active && (
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-teal-500 rounded-r" />
        )}
        <Link
          to={`/list/${list.id}`}
          className={`flex items-center gap-3 w-full py-2 px-3 rounded-lg transition-colors text-sm ${
            isCollapsed ? 'justify-center' : ''
          } ${
            active
              ? 'text-teal-300 bg-teal-500/10'
              : 'text-zinc-400 hover:text-white hover:bg-[#1A1F26]/30'
          }`}
        >
          <Users size={16} className={`shrink-0 ${active ? 'text-teal-300' : 'text-zinc-500'}`} />
          {!isCollapsed && (
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <span className="truncate">{list.name}</span>
              {list.item_count > 0 && (
                <span className="text-xs text-zinc-400 tabular-nums shrink-0">{list.item_count}</span>
              )}
            </span>
          )}
        </Link>
        {isCollapsed && <TooltipPortal label={tooltipLabel} targetRect={tooltipRect} />}
      </div>
    );
  };

  // Followed list item component
  const FollowedListItem = ({
    list,
    active,
  }: {
    list: FollowedList;
    active: boolean;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
    // Defensive: if no owner.username, show just list name (avoids "@'s ListName")
    const displayLabel = list.owner?.username
      ? `@${list.owner.username}'s ${list.name}`
      : list.name;

    const handleMouseEnter = () => {
      if (isCollapsed && itemRef.current) {
        setTooltipRect(itemRef.current.getBoundingClientRect());
      }
      // Prefetch list detail on hover
      prefetchList(list.id);
    };

    const handleMouseLeave = () => {
      setTooltipRect(null);
      cancelListPrefetch();
    };

    return (
      <div
        ref={itemRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {active && (
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-teal-500 rounded-r" />
        )}
        <Link
          to={`/list/${list.id}`}
          className={`flex items-center gap-3 w-full py-2 px-3 rounded-lg transition-colors text-sm ${
            isCollapsed ? 'justify-center' : ''
          } ${
            active
              ? 'text-teal-300 bg-teal-500/10'
              : 'text-zinc-400 hover:text-white hover:bg-[#1A1F26]/30'
          }`}
        >
          {/* Owner avatar */}
          <div className="w-5 h-5 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-teal-400 to-orange-500">
            {list.owner?.avatar_url ? (
              <img
                src={list.owner.avatar_url}
                alt={list.owner.username || list.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User size={10} className="text-white" />
              </div>
            )}
          </div>
          {!isCollapsed && (
            <span className="truncate flex-1 text-left whitespace-nowrap">
              {displayLabel}
            </span>
          )}
        </Link>
        {isCollapsed && <TooltipPortal label={displayLabel} targetRect={tooltipRect} />}
      </div>
    );
  };

  return (
    <>
      <aside
        style={{ fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
        className={`hidden md:flex flex-col h-screen fixed left-0 top-0 bottom-0 z-30 bg-[#0F1419] border-r border-white/[0.06] transition-all duration-200 overflow-hidden ${
          isCollapsed ? 'w-[72px]' : 'w-[260px]'
        }`}
      >
        {/* Header - fixed, never scrolls */}
        <div
          className="shrink-0 relative h-16 border-b border-white/[0.06]"
        >
          {/* Centered brand (prevents "stuck to the left" look) */}
          <Link
            to="/"
            onClick={expandSidebar}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            aria-label="Filmple home"
          >
            <img
              src={isCollapsed ? '/logo-icon.png' : '/logo.png'}
              alt="Filmple"
              className={isCollapsed ? 'h-11 w-auto object-contain' : 'h-12 w-auto object-contain'}
            />
          </Link>

          {/* Collapse control (kept on the right) */}
          {!isCollapsed && (
            <button
              onClick={toggleCollapsed}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-white transition-colors"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft size={20} />
            </button>
          )}
        </div>

        {/* Middle content - scrollable area */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-2">
          {/* Main Navigation */}
          <nav className="px-2 space-y-1">
            <NavItem to="/" icon={Compass} label="Discover" active={isActive('/')} />
            <NavItem to="/for-you" icon={User} label="For You" active={isActive('/for-you')} />
            <NavItem to="/feel" icon={Sparkles} label="I Feel" active={isActive('/feel')} />
            <SocialNavItem
              icon={Users}
              label="Social"
              active={socialPanelOpen}
              onClick={() => setSocialPanelOpen(true)}
            />
            <NavItem to="/filmply" icon={MessageSquare} label="Filmply" active={isActive('/filmply')} />
          </nav>

          {/* Library Section - Only show if user is logged in */}
          {user && (
            <>
              {/* Library header - PART B: Collapsible */}
              {!isCollapsed ? (
                <div className="flex items-center justify-between px-4 mt-6 mb-2">
                  <button
                    onClick={() => toggleSection('library')}
                    className="flex items-center gap-1 group"
                    aria-expanded={sectionsOpen.library}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Library</span>
                    <ChevronDown
                      size={14}
                      className={`text-zinc-500 group-hover:text-zinc-300 transition-transform ${
                        sectionsOpen.library ? '' : '-rotate-90'
                      }`}
                    />
                  </button>
                </div>
              ) : (
                <div className="mx-3 mt-4 mb-2 border-t border-white/[0.06]" />
              )}

              {/* PART B: Library items - only render if section is open */}
              {sectionsOpen.library && (
              <nav className="px-2 space-y-1">
                {watchlistId && (
                  <LibraryItem
                    to={`/list/${watchlistId}`}
                    icon={Bookmark}
                    label="Watchlist"
                    count={getDefaultListCount('watchlist', watchlistList?.item_count ?? watchlistCount)}
                    active={isListActive(watchlistId)}
                    listId={watchlistId}
                  />
                )}
                {watchedId && (
                  <LibraryItem
                    to={`/list/${watchedId}`}
                    icon={CheckCircle}
                    label="Watched"
                    count={getDefaultListCount('watched', watchedList?.item_count ?? watchedCount)}
                    active={isListActive(watchedId)}
                    listId={watchedId}
                  />
                )}
                {favoritesId && (
                  <LibraryItem
                    to={`/list/${favoritesId}`}
                    icon={Heart}
                    label="Favorites"
                    count={getDefaultListCount('favorites', favoritesList?.item_count ?? favoritesCount)}
                    active={isListActive(favoritesId)}
                    listId={favoritesId}
                  />
                )}
              </nav>
              )}

              {/* Your Lists header - PART B: Collapsible */}
              {!isCollapsed ? (
                <div className="flex items-center justify-between px-4 mt-6 mb-2">
                  <button
                    onClick={() => toggleSection('yourLists')}
                    className="flex items-center gap-1 group"
                    aria-expanded={sectionsOpen.yourLists}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Your Lists</span>
                    <ChevronDown
                      size={14}
                      className={`text-zinc-500 group-hover:text-zinc-300 transition-transform ${
                        sectionsOpen.yourLists ? '' : '-rotate-90'
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => setShowCreateListModal(true)}
                    className="text-zinc-400 hover:text-white transition-colors"
                    title="Create new list"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              ) : (
                (ownedCustomLists.length > 0 || sharedCustomLists.length > 0) && <div className="mx-3 mt-4 mb-2 border-t border-white/[0.06]" />
              )}

              {/* PART B: Your Lists items - only render if section is open */}
              {sectionsOpen.yourLists && (
                <>
                  {/* Custom lists (owned) */}
                  {ownedCustomLists.length > 0 && (
                    <div className="px-2 space-y-0.5">
                      {ownedCustomLists.map((list) => (
                        <CustomListItem
                          key={list.id}
                          list={list}
                          active={isListActive(list.id)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Create first list button */}
                  {ownedCustomLists.length === 0 && !isCollapsed && (
                    <button
                      onClick={() => setShowCreateListModal(true)}
                      className="flex items-center gap-3 w-full py-2 px-5 text-sm text-zinc-400 hover:text-teal-400 transition-colors"
                    >
                      <Plus size={16} className="shrink-0" />
                      Create a list
                    </button>
                  )}
                </>
              )}

              {/* Shared Lists section - PART B: Collapsible */}
              {sharedCustomLists.length > 0 && (
                <>
                  {!isCollapsed ? (
                    <button
                      onClick={() => toggleSection('sharedLists')}
                      className="flex items-center gap-1 w-full px-4 mt-6 mb-2 group"
                      aria-expanded={sectionsOpen.sharedLists}
                    >
                      <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Shared Lists</span>
                      <ChevronDown
                        size={14}
                        className={`text-zinc-500 group-hover:text-zinc-300 transition-transform ${
                          sectionsOpen.sharedLists ? '' : '-rotate-90'
                        }`}
                      />
                    </button>
                  ) : (
                    <div className="mx-3 mt-4 mb-2 border-t border-white/[0.06]" />
                  )}

                  {/* PART B: Shared Lists items - only render if section is open */}
                  {sectionsOpen.sharedLists && (
                    <div className="px-2 space-y-0.5">
                      {sharedCustomLists.map((list) => (
                        <SharedListItem
                          key={list.id}
                          list={list}
                          active={isListActive(list.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Following section - PART B: Collapsible */}
              {followedListsToRender.length > 0 && (
                <>
                  {!isCollapsed ? (
                    <button
                      onClick={() => toggleSection('following')}
                      className="flex items-center gap-1 w-full px-4 mt-6 mb-2 group"
                      aria-expanded={sectionsOpen.following}
                    >
                      <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Following</span>
                      <ChevronDown
                        size={14}
                        className={`text-zinc-500 group-hover:text-zinc-300 transition-transform ${
                          sectionsOpen.following ? '' : '-rotate-90'
                        }`}
                      />
                    </button>
                  ) : (
                    <div className="mx-3 mt-4 mb-2 border-t border-white/[0.06]" />
                  )}

                  {/* PART B: Following items - only render if section is open */}
                  {sectionsOpen.following && (
                    <div className="px-2 space-y-0.5 pb-4">
                      {followedListsToRender.map((list) => (
                        <FollowedListItem
                          key={list.id}
                          list={list}
                          active={isListActive(list.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer - Signed-out legal links */}
        {!user && !isCollapsed && (
          <div className="shrink-0 border-t border-white/[0.06] p-2">
            <nav className="space-y-0.5">
              <Link
                to="/terms"
                className="block px-2 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-[#1A1F26]/50 rounded-md transition-colors"
              >
                Terms and Conditions
              </Link>
              <Link
                to="/privacy-policy"
                className="block px-2 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-[#1A1F26]/50 rounded-md transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/cookie-policy"
                className="block px-2 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-[#1A1F26]/50 rounded-md transition-colors"
              >
                Cookie Policy
              </Link>
              <Link
                to="/disclaimer"
                className="block px-2 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-[#1A1F26]/50 rounded-md transition-colors"
              >
                Disclaimer
              </Link>
            </nav>
            <div className="mt-3 px-2 text-[11px] leading-5 text-zinc-500">
              <p className="font-medium text-zinc-400">Filmpl LLC</p>
              <p>16192 Coastal Highway</p>
              <p>Lewes, DE 19958</p>
              <p>United States</p>
            </div>
          </div>
        )}

        {/* Footer - Expand/Collapse button (when collapsed) */}
        {isCollapsed && (
          <div className="shrink-0 border-t border-white/[0.06] p-2">
            <div
              ref={expandBtnRef}
              className="relative"
              onMouseEnter={() => expandBtnRef.current && setExpandTooltipRect(expandBtnRef.current.getBoundingClientRect())}
              onMouseLeave={() => setExpandTooltipRect(null)}
            >
              <button
                onClick={toggleCollapsed}
                className="w-full p-2 text-zinc-400 hover:text-white hover:bg-[#1A1F26]/50 rounded-lg transition-colors flex items-center justify-center"
              >
                <ChevronRight size={20} />
              </button>
              <TooltipPortal label="Expand" targetRect={expandTooltipRect} />
            </div>
          </div>
        )}

        {/* Footer - User section */}
        {user && profile && (
          <div className="shrink-0 border-t border-white/[0.06] p-2">
            <div
              ref={userBtnRef}
              className="relative"
              onMouseEnter={() => isCollapsed && userBtnRef.current && setUserTooltipRect(userBtnRef.current.getBoundingClientRect())}
              onMouseLeave={() => setUserTooltipRect(null)}
            >
              <Link
                to={profile.username ? `/u/${profile.username}` : '#'}
                className={`flex items-center gap-3 p-2 rounded-lg text-slate-300 hover:text-white hover:bg-[#1A1F26]/50 transition-colors ${
                  isCollapsed ? 'justify-center' : ''
                }`}
              >
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-teal-400 to-orange-500 flex items-center justify-center">
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.username || 'User'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-sm font-medium">{getUserInitials()}</span>
                  )}
                </div>
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {profile.display_name || profile.username || 'User'}
                    </p>
                    {profile.username && (
                      <p className="text-xs text-zinc-400 truncate">@{profile.username}</p>
                    )}
                  </div>
                )}
              </Link>
              {isCollapsed && <TooltipPortal label={profile.display_name || profile.username || 'Profile'} targetRect={userTooltipRect} />}
            </div>
          </div>
        )}

        {/* Social Panel Overlay */}
        {socialPanelOpen && !isCollapsed && (
          <div className="absolute inset-0 z-50 bg-[#0F1419]">
            <SocialPanel onClose={() => setSocialPanelOpen(false)} />
          </div>
        )}
      </aside>

      {/* Create List Modal */}
      <CreateListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onCreated={handleListCreated}
        createWatchlist={createWatchlist}
      />
    </>
  );
}
